import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  ClipboardList, 
  Plus, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Search,
  Filter,
  Building2,
  CheckCircle,
  Clock,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const repairTypes = [
  "Engine Repair",
  "Transmission",
  "Electrical",
  "Bodywork",
  "Suspension",
  "Brake System",
  "Cooling System",
  "Fuel System",
  "Exhaust System",
  "General Maintenance",
  "Other"
];

const priorityOptions = [
  { value: "low", label: "Low", className: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
  { value: "normal", label: "Normal", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "high", label: "High", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { value: "urgent", label: "Urgent", className: "bg-red-500/20 text-red-400 border-red-500/30" }
];

const statusOptions = [
  { value: "pending", label: "Pending", className: "bg-gray-500/20 text-gray-400 border-gray-500/30", icon: Clock },
  { value: "in_progress", label: "In Progress", className: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: AlertCircle },
  { value: "completed", label: "Completed", className: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle },
  { value: "cancelled", label: "Cancelled", className: "bg-red-500/20 text-red-400 border-red-500/30", icon: AlertCircle }
];

const formatCurrency = (value) => {
  if (!value) return "-";
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function RepairOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [workshopDialogOpen, setWorkshopDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    vehicle_id: "",
    workshop_id: "",
    description: "",
    repair_type: "Engine Repair",
    estimated_cost: 0,
    reported_issue: "",
    priority: "normal",
    scheduled_date: "",
    organization_id: ""
  });

  const [editFormData, setEditFormData] = useState({
    status: "",
    actual_cost: 0,
    completion_date: "",
    notes: "",
    assigned_mechanic: ""
  });

  const [workshopFormData, setWorkshopFormData] = useState({
    name: "",
    address: "",
    contact_phone: "",
    contact_email: "",
    is_primary: false,
    organization_id: ""
  });

  const canEdit = user?.role === "admin" || user?.role === "fleet_manager";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, vehiclesRes, workshopsRes, orgsRes] = await Promise.all([
        apiClient.get("/repair-orders"),
        apiClient.get("/vehicles"),
        apiClient.get("/workshops"),
        apiClient.get("/organizations")
      ]);
      setOrders(ordersRes.data);
      setVehicles(vehiclesRes.data);
      setWorkshops(workshopsRes.data);
      setOrganizations(orgsRes.data);
      
      if (vehiclesRes.data.length > 0 && !formData.vehicle_id) {
        setFormData(prev => ({ 
          ...prev, 
          vehicle_id: vehiclesRes.data[0].id,
          organization_id: vehiclesRes.data[0].organization_id
        }));
      }
      if (workshopsRes.data.length > 0 && !formData.workshop_id) {
        const primaryWorkshop = workshopsRes.data.find(w => w.is_primary) || workshopsRes.data[0];
        setFormData(prev => ({ ...prev, workshop_id: primaryWorkshop.id }));
      }
      if (orgsRes.data.length > 0 && !workshopFormData.organization_id) {
        setWorkshopFormData(prev => ({ ...prev, organization_id: orgsRes.data[0].id }));
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/repair-orders", formData);
      toast.success("Repair order created successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setEditFormData({
      status: order.status,
      actual_cost: order.actual_cost || 0,
      completion_date: order.completion_date || "",
      notes: order.notes || "",
      assigned_mechanic: order.assigned_mechanic || ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put(`/repair-orders/${editingOrder.id}`, editFormData);
      toast.success("Repair order updated successfully");
      setEditDialogOpen(false);
      setEditingOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    }
  };

  const handleDelete = async (orderId) => {
    if (!confirm("Are you sure you want to delete this repair order?")) return;
    try {
      await apiClient.delete(`/repair-orders/${orderId}`);
      toast.success("Repair order deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete order");
    }
  };

  const handleCreateWorkshop = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/workshops", workshopFormData);
      toast.success("Workshop created successfully");
      setWorkshopDialogOpen(false);
      setWorkshopFormData({
        name: "",
        address: "",
        contact_phone: "",
        contact_email: "",
        is_primary: false,
        organization_id: organizations[0]?.id || ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create workshop");
    }
  };

  const resetForm = () => {
    const defaultVehicle = vehicles[0];
    const primaryWorkshop = workshops.find(w => w.is_primary) || workshops[0];
    setFormData({
      vehicle_id: defaultVehicle?.id || "",
      workshop_id: primaryWorkshop?.id || "",
      description: "",
      repair_type: "Engine Repair",
      estimated_cost: 0,
      reported_issue: "",
      priority: "normal",
      scheduled_date: "",
      organization_id: defaultVehicle?.organization_id || ""
    });
  };

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setFormData({ 
      ...formData, 
      vehicle_id: vehicleId,
      organization_id: vehicle?.organization_id || ""
    });
  };

  const filteredOrders = orders.filter(order => {
    const vehicle = vehicles.find(v => v.id === order.vehicle_id);
    const workshop = workshops.find(w => w.id === order.workshop_id);
    const vehicleInfo = vehicle ? `${vehicle.registration_number} ${vehicle.make} ${vehicle.model}` : "";
    const workshopName = workshop?.name || "";
    const matchesSearch = 
      order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicleInfo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workshopName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.repair_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.registration_number} - ${vehicle.make} ${vehicle.model}` : "Unknown";
  };

  const getWorkshopName = (workshopId) => {
    const workshop = workshops.find(w => w.id === workshopId);
    return workshop?.name || "Unknown";
  };

  const getStatusBadge = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const getPriorityBadge = (priority) => {
    return priorityOptions.find(p => p.value === priority) || priorityOptions[1];
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="repair-orders-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="skeleton-tactical h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="repair-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            REPAIR ORDERS
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Manage job orders for workshops
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <Dialog open={workshopDialogOpen} onOpenChange={setWorkshopDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-border text-foreground hover:bg-accent rounded-sm font-mono text-xs" data-testid="add-workshop-btn">
                    <Building2 className="w-4 h-4 mr-2" />
                    ADD WORKSHOP
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border rounded-sm max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-lg tracking-wide">
                      ADD WORKSHOP
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateWorkshop} className="space-y-4" data-testid="workshop-form">
                    <div className="space-y-2">
                      <Label className="text-xs font-mono text-muted-foreground uppercase">Name</Label>
                      <Input
                        value={workshopFormData.name}
                        onChange={(e) => setWorkshopFormData({ ...workshopFormData, name: e.target.value })}
                        placeholder="Workshop name"
                        className="bg-input border-border rounded-sm font-mono text-sm"
                        required
                        data-testid="workshop-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono text-muted-foreground uppercase">Address</Label>
                      <Input
                        value={workshopFormData.address}
                        onChange={(e) => setWorkshopFormData({ ...workshopFormData, address: e.target.value })}
                        placeholder="Workshop address"
                        className="bg-input border-border rounded-sm font-mono text-sm"
                        data-testid="workshop-address-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Phone</Label>
                        <Input
                          value={workshopFormData.contact_phone}
                          onChange={(e) => setWorkshopFormData({ ...workshopFormData, contact_phone: e.target.value })}
                          placeholder="+255..."
                          className="bg-input border-border rounded-sm font-mono text-sm"
                          data-testid="workshop-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Email</Label>
                        <Input
                          value={workshopFormData.contact_email}
                          onChange={(e) => setWorkshopFormData({ ...workshopFormData, contact_email: e.target.value })}
                          placeholder="email@..."
                          className="bg-input border-border rounded-sm font-mono text-sm"
                          data-testid="workshop-email-input"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="workshop-submit-btn">
                      ADD WORKSHOP
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="add-repair-order-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    NEW ORDER
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border rounded-sm max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-lg tracking-wide">
                      CREATE REPAIR ORDER
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="repair-order-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Vehicle</Label>
                        <Select value={formData.vehicle_id} onValueChange={handleVehicleChange}>
                          <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="order-vehicle-select">
                            <SelectValue placeholder="Select vehicle" />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border rounded-sm">
                            {vehicles.map(vehicle => (
                              <SelectItem key={vehicle.id} value={vehicle.id} className="font-mono text-sm">
                                {vehicle.registration_number} - {vehicle.make}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Workshop</Label>
                        <Select value={formData.workshop_id} onValueChange={(value) => setFormData({ ...formData, workshop_id: value })}>
                          <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="order-workshop-select">
                            <SelectValue placeholder="Select workshop" />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border rounded-sm">
                            {workshops.map(workshop => (
                              <SelectItem key={workshop.id} value={workshop.id} className="font-mono text-sm">
                                {workshop.name} {workshop.is_primary && "(Primary)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Repair Type</Label>
                        <Select value={formData.repair_type} onValueChange={(value) => setFormData({ ...formData, repair_type: value })}>
                          <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="order-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border rounded-sm">
                            {repairTypes.map(type => (
                              <SelectItem key={type} value={type} className="font-mono text-sm">{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Priority</Label>
                        <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                          <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="order-priority-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border rounded-sm">
                            {priorityOptions.map(option => (
                              <SelectItem key={option.value} value={option.value} className="font-mono text-sm">{option.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-mono text-muted-foreground uppercase">Reported Issue</Label>
                      <Textarea
                        value={formData.reported_issue}
                        onChange={(e) => setFormData({ ...formData, reported_issue: e.target.value })}
                        placeholder="Describe the issue reported..."
                        className="bg-input border-border rounded-sm font-mono text-sm min-h-[60px]"
                        required
                        data-testid="order-issue-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-mono text-muted-foreground uppercase">Work Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe work to be done..."
                        className="bg-input border-border rounded-sm font-mono text-sm min-h-[60px]"
                        required
                        data-testid="order-description-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Estimated Cost (TZS)</Label>
                        <Input
                          type="number"
                          value={formData.estimated_cost}
                          onChange={(e) => setFormData({ ...formData, estimated_cost: parseFloat(e.target.value) })}
                          className="bg-input border-border rounded-sm font-mono text-sm"
                          data-testid="order-cost-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Scheduled Date</Label>
                        <Input
                          type="date"
                          value={formData.scheduled_date}
                          onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                          className="bg-input border-border rounded-sm font-mono text-sm"
                          data-testid="order-date-input"
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="order-submit-btn">
                      CREATE REPAIR ORDER
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-foreground">{orders.length}</p>
            <p className="text-xs font-mono text-muted-foreground">TOTAL ORDERS</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-gray-400">{orders.filter(o => o.status === "pending").length}</p>
            <p className="text-xs font-mono text-muted-foreground">PENDING</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-blue-400">{orders.filter(o => o.status === "in_progress").length}</p>
            <p className="text-xs font-mono text-muted-foreground">IN PROGRESS</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-green-400">{orders.filter(o => o.status === "completed").length}</p>
            <p className="text-xs font-mono text-muted-foreground">COMPLETED</p>
          </CardContent>
        </Card>
      </div>

      {/* Workshops List */}
      {workshops.length > 0 && (
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="font-heading text-sm tracking-wide text-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              REGISTERED WORKSHOPS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3">
              {workshops.map(workshop => (
                <div key={workshop.id} className={`px-3 py-2 rounded-sm border ${workshop.is_primary ? "border-primary bg-primary/10" : "border-border bg-accent/30"}`}>
                  <p className="text-sm font-medium text-foreground">{workshop.name}</p>
                  {workshop.is_primary && <span className="text-[10px] font-mono text-primary">PRIMARY</span>}
                  {workshop.contact_phone && <p className="text-xs font-mono text-muted-foreground mt-1">{workshop.contact_phone}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-input border-border rounded-sm font-mono text-sm"
                data-testid="order-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-border rounded-sm font-mono text-sm" data-testid="order-filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="all" className="font-mono text-sm">All Statuses</SelectItem>
                {statusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value} className="font-mono text-sm">{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="bg-card border-border rounded-sm" data-testid="repair-orders-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">ORDER #</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">VEHICLE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">WORKSHOP</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">TYPE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">PRIORITY</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">STATUS</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">EST. COST</TableHead>
                  {canEdit && <TableHead className="font-heading text-xs tracking-wide text-muted-foreground text-right">ACTIONS</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const statusBadge = getStatusBadge(order.status);
                  const priorityBadge = getPriorityBadge(order.priority);
                  return (
                    <TableRow key={order.id} className="border-border hover:bg-accent/50" data-testid={`order-row-${order.id}`}>
                      <TableCell className="font-mono text-sm font-medium text-primary">
                        {order.order_number}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-foreground">
                        {getVehicleInfo(order.vehicle_id)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {getWorkshopName(order.workshop_id)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {order.repair_type}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${priorityBadge.className} rounded-sm font-mono text-xs border`}>
                          {priorityBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusBadge.className} rounded-sm font-mono text-xs border`}>
                          {statusBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-yellow-400">
                        {formatCurrency(order.estimated_cost)}
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`order-actions-${order.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
                              <DropdownMenuItem onClick={() => handleEdit(order)} className="cursor-pointer">
                                <Edit className="w-4 h-4 mr-2" />
                                Update Status
                              </DropdownMenuItem>
                              {user?.role === "admin" && (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(order.id)} 
                                  className="cursor-pointer text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {filteredOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-muted-foreground font-mono">
                      No repair orders found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Status Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg tracking-wide">
              UPDATE ORDER: {editingOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4" data-testid="edit-order-form">
            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground uppercase">Status</Label>
              <Select value={editFormData.status} onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}>
                <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="edit-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border rounded-sm">
                  {statusOptions.map(option => (
                    <SelectItem key={option.value} value={option.value} className="font-mono text-sm">{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground uppercase">Assigned Mechanic</Label>
              <Input
                value={editFormData.assigned_mechanic}
                onChange={(e) => setEditFormData({ ...editFormData, assigned_mechanic: e.target.value })}
                placeholder="Mechanic name"
                className="bg-input border-border rounded-sm font-mono text-sm"
                data-testid="edit-mechanic-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-mono text-muted-foreground uppercase">Actual Cost (TZS)</Label>
                <Input
                  type="number"
                  value={editFormData.actual_cost}
                  onChange={(e) => setEditFormData({ ...editFormData, actual_cost: parseFloat(e.target.value) })}
                  className="bg-input border-border rounded-sm font-mono text-sm"
                  data-testid="edit-cost-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-mono text-muted-foreground uppercase">Completion Date</Label>
                <Input
                  type="date"
                  value={editFormData.completion_date}
                  onChange={(e) => setEditFormData({ ...editFormData, completion_date: e.target.value })}
                  className="bg-input border-border rounded-sm font-mono text-sm"
                  data-testid="edit-completion-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground uppercase">Notes</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Add notes..."
                className="bg-input border-border rounded-sm font-mono text-sm min-h-[80px]"
                data-testid="edit-notes-input"
              />
            </div>

            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="edit-submit-btn">
              UPDATE ORDER
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
